/**
 * Plan 20 P20.3 (lane C): per-level ambient particles + the L3 attractor ember pilot.
 *
 * TWO paths, built in the plan's order:
 *  1. QUAD PATH (this file's pools) — per-level ambient particles as ONE pooled thin-instance quad
 *     mesh per level, driven PURE-f(snapshot) (the debris.ts pooling pattern): L1 'overcast' drizzle
 *     streaks, L2 'industrial' ash flakes, L3 'dusk' dust + fallback embers. High tier ONLY (+1 draw
 *     per level, `alwaysSelectAsActiveMesh` — precedent fx.ts QuadBatch / debris.ts / world.ts
 *     horizon). Billboard orientation is recomputed from the snapshot EVERY frame into the pooled
 *     matrix buffer, so a paused frame is frozen exactly (fx freeze rule) and a replay matches.
 *     This path is ALSO the pilot's fallback: when the pilot ships OFF, L3's embers merge INTO the
 *     dust pool and L3 stays ONE ambient mesh (+1 draw, expected delta per the plan's table).
 *  2. ATTRACTOR PILOT (L3 only, High only, +1 more draw; +2 counted on WebGL2 with the
 *     transform-feedback update pass — webgl2ParticleSystem.pure.js:226 → _reportDrawCall): ONE
 *     GPUParticleSystem per L3 run whose box emitter tracks squadZ f(snapshot) and whose ≤ 8
 *     attractors are repositioned EVERY frame to the visible braziers (nearest-8-by-z). Attractor
 *     strength is POSITIVE = pull toward the brazier (attractor.d.ts; the gpu update shader adds
 *     (strength/(d²+1))·normalize(toAttractor)·timeDelta — Shaders/gpuUpdateParticles.vertex.js),
 *     layered over the embers' upward drift velocity. `blendMode` is PINNED to the DEFAULT
 *     BLENDMODE_ONEONE — never BLENDMODE_MULTIPLYADD, which takes a two-draw render path
 *     (gpuParticleSystem.pure.js:2037). The pilot is DROPPABLE: kill criteria (a)–(e) in plan 20
 *     Goal 5; any one firing ships it OFF and the quad fallback above is the shipped L3 look.
 *
 * BRAZIER SLOT → WORLD Z MAPPING (lane A's `brazierPlacements(index)` is per RING SLOT, this
 * system is per LEVEL — the mapping lives here and is f(snapshot), never render-side state):
 *   - world.ts's Bridge recycles 40 m segments: a root is moved forward when
 *     `root.position.z + SEGMENT_LENGTH < squadZ - 30` (world.ts update()), so after every update
 *     the occupied blocks are exactly [kMin, kMin + segmentCount - 1] with
 *     `kMin = max(0, ceil((squadZ - 70) / 40))` (70 = the 40 m segment + the Bridge's 30 m
 *     behind-margin). `segmentBlockStart(squadZ)` below reproduces that converged state purely
 *     from squadZ — pinned against a real Bridge by ambient.test.ts.
 *   - A root built from `buildDressing(scene, index)` initially sits at block `index` and roots
 *     recycle in root order, so the ring slot of block k is `k mod segmentCount` (k ≡ index
 *     (mod segmentCount) in both the unmoved and recycled cases). Therefore the braziers on
 *     block k are `brazierPlacements(k mod segmentCount)` at world z = 40·k + placement.z —
 *     exactly `squadZ + slot offset`, pure f(snapshot).
 *
 * FREEZE CARVE-OUT (digest 3(c), owner-approved): the pilot's pose is render-history-dependent,
 * so it freezes via `updateSpeed = 0` while the sim is paused instead of the pure-f rule. The
 * pause-state WIRING is lane D's (main.ts calls `ambient.setFrozen(simPaused)`); the freeze
 * mechanism and the call inside this system's update are lane C's, with the hook INJECTABLE so
 * the state machine is test-pinned without ever constructing a GPUParticleSystem under NullEngine
 * (the ENV_THEME_PARAMS precedent — NullEngine cannot construct one anyway: no transform-feedback
 * caps, verified).
 */
import {
  Attractor,
  BaseParticleSystem,
  Color3,
  Color4,
  Constants,
  MeshBuilder,
  StandardMaterial,
  Vector3,
} from '@babylonjs/core/pure';
// IMPORTANT: GPUParticleSystem must come from the NON-pure modules. `gpuParticleSystem.pure.js`
// skips the shader side-effect imports, so at runtime 'gpuRenderParticles' is missing from the
// ShaderStore and vite's SPA fallback serves index.html as the "shader" source. The non-pure
// wrapper re-exports the same class AND imports ../Shaders/gpuRenderParticles.{fragment,vertex}.js.
// The constructor ALSO looks up the engine-specific platform class ("BABYLON.WebGL2ParticleSystem")
// in the class registry and throws "The WebGL2ParticleSystem class is not available!" without it —
// webgl2ParticleSystem.js registers it AND imports the gpuUpdateParticles.{fragment,vertex}.js
// update shaders the transform-feedback pass needs (verified: the gpuParticleSystem.js import alone
// still throws under the WebGL2 inspect).
import { GPUParticleSystem } from '@babylonjs/core/Particles/gpuParticleSystem.js';
import '@babylonjs/core/Particles/webgl2ParticleSystem.js';
import type { DynamicTexture, Mesh, Scene } from '@babylonjs/core/pure';
import { hashValue } from './scene';
import type { EnvTheme } from './scene';
import type { QualityTier } from '../gfx/quality';
import { buildSpriteTexture } from './fx';
import { brazierPlacements, SHOULDER_OUT } from './dressing';
import type { BrazierPlacement } from './dressing';
import { SEGMENT_LENGTH } from './world';
import { toWorldX } from './actors';
import type { SimState } from '../sim/sim';

// ---------------------------------------------------------------------------
// Budget constants (plan 20 design rule): `ambientPoolSize × 2 + emberCapacity × 2 ≤ 9,900` —
// a GPUParticleSystem renders capacity × 2 tris via its 4-vertex triangle strip
// (MATERIAL_TriangleStripDrawMode, gpuParticleSystem.pure.js:1758). 1500 × 2 + 1200 × 2 = 5,400.
// inspect's tri counter may not count particle quads (mesh active-indices based), so this
// constant-level test IS the pilot's triangle bound, stated as such.
// ---------------------------------------------------------------------------

/** Thin-instance quad capacity of the ONE ambient mesh per level. */
export const AMBIENT_POOL_SIZE = 1500;
/** GPUParticleSystem capacity of the L3 ember pilot (one system per L3 run). */
export const EMBER_CAPACITY = 1200;
/** Dusk dust motes in the shared pool; the rest of the pool carries the fallback embers. */
export const DUSK_DUST_COUNT = 1200;
/** Fallback embers merged into the dusk pool when the pilot is OFF (pool stays ONE mesh). */
export const EMBER_FALLBACK_COUNT = AMBIENT_POOL_SIZE - DUSK_DUST_COUNT;

/** Live particles per theme (the pool buffer stays at AMBIENT_POOL_SIZE on every level). */
const THEME_PARTICLE_COUNT: Record<EnvTheme, number> = {
  overcast: 1400, // drizzle streaks
  industrial: 1000, // ash flakes
  dusk: DUSK_DUST_COUNT, // dust motes (+ the fallback embers when the pilot is OFF)
};

// The ambient field rides the squad (like the horizon): a z-window around squadZ, an x-window
// across the bridge + shoulders, and a y-wrap so falling particles recycle deterministically.
export const AMBIENT_BACK = 10; // metres behind the squad — BEHIND the chase camera (squadZ − 8), so the
// world-anchored wrap's vanish plane (at squadZ − AMBIENT_BACK) is never on screen (P20-5 follow-up:
// at 6 the plane sat 2 m ahead of the lens and a particle could pop visibly; opus P1-A).
export const AMBIENT_AHEAD = 54; // metres ahead (the gameplay frustum reach)
export const AMBIENT_SPAN = AMBIENT_BACK + AMBIENT_AHEAD;
export const AMBIENT_X_SPAN = 10; // |x| ≤ 10 m: the deck (±4.5), shoulders (to ±7.3) + margin
export const AMBIENT_TOP = 7.5;
export const AMBIENT_HEIGHT = 8; // y wraps in [AMBIENT_TOP − 8, AMBIENT_TOP] = [−0.5, 7.5]

/** The Bridge's recycle margin (world.ts: a root is recycled when rootZ + 40 < squadZ − 30). */
const RECYCLE_MARGIN = SEGMENT_LENGTH + 30;

/** Positive modulo (JS % keeps the dividend's sign) — the world-anchored z wrap below. */
const mod = (v: number, m: number): number => ((v % m) + m) % m;

/**
 * World z where the OLDEST alive 40 m block starts, after the Bridge's update(squadZ) has
 * converged — pure f(squadZ), the documented slot→world mapping above. Pinned against a real
 * Bridge walk by ambient.test.ts (if world.ts's recycle rule ever changes, this test fails here,
 * not silently in a screenshot).
 */
export function segmentBlockStart(squadZ: number): number {
  const k = Math.ceil((squadZ - RECYCLE_MARGIN) / SEGMENT_LENGTH);
  return Math.max(0, k) * SEGMENT_LENGTH;
}

/** World-space brazier placements over every alive block (2 per block, one per side). */
export function brazierWorldPlacements(squadZ: number, segmentCount: number): BrazierPlacement[] {
  const kMin = Math.max(0, Math.ceil((squadZ - RECYCLE_MARGIN) / SEGMENT_LENGTH));
  const out: BrazierPlacement[] = [];
  for (let k = kMin; k < kMin + segmentCount; k++) {
    for (const b of brazierPlacementsCached(k % segmentCount)) {
      out.push({ x: b.x, z: k * SEGMENT_LENGTH + b.z, topY: b.topY });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Closed-form particle poses (pure f(snapshot) — the burstParticle/shardPose contract).
// ---------------------------------------------------------------------------

/**
 * Per-particle hash channel (P20-5: module-level so the pose functions below don't allocate a
 * closure per particle per frame). Same values as the old inline `h` closures — pure rename.
 */
const h = (i: number, seed: number, n: number): number => hashValue(i, seed, n);

/** Wrapped-fall y (module-level: no per-call closure — the P20-5 follow-up to the closure hoist). */
const fallY = (phase: number, t: number, speed: number): number =>
  AMBIENT_TOP - ((phase * AMBIENT_HEIGHT + t * speed) % AMBIENT_HEIGHT);

/**
 * `brazierPlacements` is pure per ring-slot index — memoized here so the per-ember rebuild stops
 * allocating an array + two placement objects per ember per frame (P20-5). Callers must treat the
 * returned placements as READ-ONLY (shared cached objects); every consumer below only reads them.
 */
const brazierCache = new Map<number, BrazierPlacement[]>();
function brazierPlacementsCached(index: number): BrazierPlacement[] {
  let cached = brazierCache.get(index);
  if (!cached) {
    cached = brazierPlacements(index);
    brazierCache.set(index, cached);
  }
  return cached;
}

/** One ambient quad's pose + tint, written by the closed forms into the pooled buffers. */
export interface AmbientQuad {
  x: number;
  y: number;
  z: number;
  /** Billboard extents (metres): sx across, sy along the camera-up axis. */
  sx: number;
  sy: number;
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * Per-level field particle i: deterministic wrap-around drift. The field NEVER dies (a falling
 * particle wraps from the top), so this always returns true — the boolean keeps the pool loop
 * uniform with the other particle functions and any future dead-particle path.
 */
export function fieldParticle(theme: EnvTheme, i: number, sim: SimState, out: AmbientQuad): boolean {
  // Per-theme hash streams (distinct seeds so the pools differ even at the same i).
  const seed = theme === 'overcast' ? 0x1d50 : theme === 'industrial' ? 0x2a50 : 0x3b50;
  // P20-5 fix — world-anchored parallax: the particle's WORLD z is fixed per h(i, seed, 1); as the
  // squad advances, particles drift through the camera window (exiting behind, re-entering ahead)
  // instead of riding squadZ. Same window as before: zOff ∈ [−AMBIENT_BACK, AMBIENT_AHEAD), so
  // out.z ∈ [squadZ − AMBIENT_BACK, squadZ + AMBIENT_AHEAD] still holds by construction.
  const zOff = mod(h(i, seed, 1) * AMBIENT_SPAN - sim.squadZ, AMBIENT_SPAN) - AMBIENT_BACK;
  const x0 = (h(i, seed, 2) - 0.5) * 2 * AMBIENT_X_SPAN;
  // Wrapped fall: y = TOP − (phase + t·speed) mod HEIGHT — pure f(sim.t), no accumulation.
  // (P20-5 follow-up: the per-call closure is hoisted to the module-level fallOf below.)
  const phase = h(i, seed, 4);
  if (theme === 'overcast') {
    // L1 drizzle: thin fast streaks, cool grey-blue, low alpha (the war-documentary haze).
    out.x = x0;
    out.y = fallY(phase, sim.t, 5.5 + h(i, seed, 3) * 2.5);
    out.z = sim.squadZ + zOff;
    out.sx = 0.022 + h(i, seed, 5) * 0.01;
    out.sy = 0.35 + h(i, seed, 6) * 0.4;
    out.r = 0.55;
    out.g = 0.63;
    out.b = 0.75;
    out.a = 0.09 + h(i, seed, 7) * 0.09;
    return true;
  }
  if (theme === 'industrial') {
    // L2 ash: slow small flakes with a hashed sway, grey through rust.
    const rust = h(i, seed, 9);
    out.x = x0 + 0.35 * Math.sin(sim.t * (0.5 + h(i, seed, 8)) + h(i, seed, 10) * Math.PI * 2);
    out.y = fallY(phase, sim.t, 0.5 + h(i, seed, 3) * 0.7);
    out.z = sim.squadZ + zOff;
    out.sx = out.sy = 0.05 + h(i, seed, 5) * 0.08;
    out.r = 0.42 + 0.1 * rust;
    out.g = 0.38 + 0.04 * rust;
    out.b = 0.34;
    out.a = 0.16 + h(i, seed, 7) * 0.16;
    return true;
  }
  // Dusk dust: near-stationary warm motes hanging in the amber haze.
  out.x = x0 + 0.25 * Math.sin(sim.t * (0.3 + h(i, seed, 8)) + h(i, seed, 10) * Math.PI * 2);
  out.y = fallY(phase, sim.t, 0.15 + h(i, seed, 3) * 0.3);
  out.z = sim.squadZ + zOff;
  out.sx = out.sy = 0.03 + h(i, seed, 5) * 0.06;
  out.r = 0.52;
  out.g = 0.43;
  out.b = 0.34;
  out.a = 0.1 + h(i, seed, 7) * 0.12;
  return true;
}

/** Fallback ember life band (s): the quad embers are shorter-lived than the pilot's. */
export const EMBER_LIFE_MIN = 1.1;
export const EMBER_LIFE_SPAN = 0.9;

/**
 * Fallback ember `slot` (0..EMBER_FALLBACK_COUNT-1) of the dusk pool: rises from the brazier it
 * is hashed to on the alive blocks, drifting up and out, fading over its life. The brazier
 * position is the documented slot→world mapping f(snapshot), so a paused frame is frozen exactly.
 */
export function emberParticle(slot: number, sim: SimState, segmentCount: number, out: AmbientQuad): boolean {
  const life = EMBER_LIFE_MIN + h(slot, 0x4c50, 1) * EMBER_LIFE_SPAN;
  const age = (h(slot, 0x4c50, 2) * life + sim.t) % life; // wrapped life: pure f(sim.t), no accumulation
  const kMin = Math.max(0, Math.ceil((sim.squadZ - RECYCLE_MARGIN) / SEGMENT_LENGTH));
  // P20-5 fix — re-phase: the block is the slot's ring-slot root's CURRENT block, so an ember
  // stays world-fixed on its brazier while that block is alive and follows the root when it
  // recycles 40·segmentCount ahead. The old `kMin + (floor(slot/2) % segmentCount)` re-phased
  // EVERY ember +40 m on every block recycle (the all-braziers-jump). k mod segmentCount is now
  // invariant per slot (≡ floor(slot/2) mod segmentCount), so a block keeps the same slot set.
  const k = kMin + ((Math.floor(slot / 2) - kMin) % segmentCount + segmentCount) % segmentCount;
  const b = brazierPlacementsCached(k % segmentCount)[slot % 2]!;
  const rise = 0.8 + h(slot, 0x4c50, 3) * 0.9;
  out.x = b.x + (h(slot, 0x4c50, 4) - 0.5) * 0.5 * age;
  out.y = b.topY + rise * age;
  out.z = k * SEGMENT_LENGTH + b.z + (h(slot, 0x4c50, 5) - 0.5) * 0.3 * age;
  out.sx = out.sy = 0.04 + h(slot, 0x4c50, 6) * 0.06;
  out.r = 1.9; // HDR red — the fallback reads under bloom like the pilot's additive embers
  out.g = 0.8;
  out.b = 0.28;
  out.a = (1 - age / life) * 0.9;
  return true;
}

// ---------------------------------------------------------------------------
// Pilot config (exported so the tests pin them without constructing anything).
// ---------------------------------------------------------------------------

/** The pilot's live update speed (≈ real time at 60 fps); restored on unfreeze. */
export const PILOT_UPDATE_SPEED = 0.016;
/** Positive attractor strength = pull toward the brazier (attractor.d.ts), layered over drift. */
export const EMBER_ATTRACT_STRENGTH = 6;
/** The blend-mode PIN: the DEFAULT ONEONE, never MULTIPLYADD (two-draw path, +4 draws). */
export const PILOT_BLEND_MODE = BaseParticleSystem.BLENDMODE_ONEONE;
/** maxAttractors is the pinned 8 (the GPU update shader's default uniform array size). */
export const PILOT_MAX_ATTRACTORS = 8;
/** Emission window relative to the tracked emitter z (= squadZ + EMIT_CENTRE_AHEAD). */
const EMIT_BACK = 28;
const EMIT_AHEAD = 28;
/** The emitter box rides this far ahead of the squad so emission follows the run. */
const EMIT_CENTRE_AHEAD = 24;
/** Visible-brazier band for the attractors (camera reach), then nearest-8-by-z. */
const ATTRACTOR_BAND_BACK = 8;
const ATTRACTOR_BAND_AHEAD = 60;

/** The dusk pool's live count: dust only under an ACTIVE pilot, dust + fallback embers otherwise. */
export function duskPoolCount(pilotActive: boolean): number {
  return pilotActive ? DUSK_DUST_COUNT : AMBIENT_POOL_SIZE;
}

/**
 * The attractor placement rule, pinned: the VISIBLE braziers (the camera band), then the
 * NEAREST-8-BY-Z when more than 8 are visible — pure f(snapshot), exported for the test.
 */
export function visibleBrazierAttractors(squadZ: number, segmentCount: number): BrazierPlacement[] {
  return brazierWorldPlacements(squadZ, segmentCount)
    .filter((b) => b.z > squadZ - ATTRACTOR_BAND_BACK && b.z < squadZ + ATTRACTOR_BAND_AHEAD)
    .sort((a, b) => Math.abs(a.z - squadZ) - Math.abs(b.z - squadZ))
    .slice(0, PILOT_MAX_ATTRACTORS);
}

/**
 * The `?pilot` runtime switch (survives the merge — the kill arms toggle it). `off` forces OFF,
 * `on` forces ON, absent takes the recorded DISPOSITION default (plan 20 Goal 5 kill arms:
 * reports/P20-3-ambient-lane.md). The param value wins over the default either way.
 */
export const PILOT_DEFAULT = false; // kill arms (c)+(d) fired environmentally — see the lane report
export function pilotEnabled(search?: string): boolean {
  const s = search ?? (typeof location !== 'undefined' ? location.search : '');
  const v = new URLSearchParams(s).get('pilot');
  if (v === 'on') return true;
  if (v === 'off') return false;
  return PILOT_DEFAULT;
}

/** The injected freeze interface: called on every state TRANSITION (setFrozen dedupes). */
export type FreezeHook = (frozen: boolean) => void;

/**
 * The production freeze hook: sets the pilot's updateSpeed to 0 while frozen and restores
 * PILOT_UPDATE_SPEED on resume (digest 3(c)). Takes the pilot as a structural `{ updateSpeed }`
 * so the state machine is test-pinnable WITHOUT a GPUParticleSystem (NullEngine cannot build one).
 */
export function pilotFreezeHook(pilot: { updateSpeed: number } | null): FreezeHook {
  return (frozen) => {
    if (pilot) pilot.updateSpeed = frozen ? 0 : PILOT_UPDATE_SPEED;
  };
}

/**
 * The per-level ambient system. ONE thin-instance quad mesh per level (always +1 draw on High,
 * 0 draws on Medium/Low — the whole class no-ops off High), plus the L3 pilot when enabled,
 * supported, on the WebGL2 backend, and a canvas-backed sprite texture exists (NullEngine never
 * constructs the pilot).
 */
export class Ambient {
  /** False off High: everything below no-ops (0 draws, 0 meshes, 0 cost). */
  readonly enabled: boolean;
  private readonly pool: Pool | null = null;
  private readonly theme: EnvTheme;
  private readonly segmentCount: number;
  private readonly dustCount: number = 0;
  private readonly pilot: GPUParticleSystem | null = null;
  private readonly attractors: Attractor[] = [];
  private readonly freezeHook: FreezeHook;
  private frozen = false;

  constructor(
    scene: Scene,
    theme: EnvTheme,
    tier: QualityTier,
    segmentCount: number,
    backend: 'webgl2' | 'webgpu',
    opts: { pilot?: boolean; onFreeze?: FreezeHook } = {},
  ) {
    this.theme = theme;
    this.segmentCount = segmentCount;
    this.enabled = tier === 'high';
    this.freezeHook = opts.onFreeze ?? pilotFreezeHook(null);
    if (!this.enabled) return;
    const sprite = buildSpriteTexture(scene); // null under NullEngine: the quad path still works,
    // the material just keeps its flat emissive (the scene.ts canvas-guard precedent).
    this.dustCount = THEME_PARTICLE_COUNT[theme];
    this.pool = new Pool(`ambient-${theme}`, scene, sprite);
    // The attractor pilot: L3 only, opt-in through the ?pilot switch (default = the recorded
    // disposition), ONLY when the engine actually supports it (kill arm (e): IsSupported on the
    // WebGL2 default) and a real sprite texture exists (NullEngine must never construct one).
    // P20-5 fix — backend guard: the pilot only builds on WebGL2. `?pilot=on` on a WebGPU engine
    // throws today (the platform-class registry has no WebGPU entry imported — only
    // webgl2ParticleSystem.js is; a WebGPU pilot needs the compute platform-class import
    // `@babylonjs/core/Particles/computeShaderParticleSystem.js` (P20-3's residual note named the
    // wrong module — webgpuParticleSystem.js does not exist in @babylonjs/core 9.28.0; the class
    // registered there is "BABYLON.ComputeShaderParticleSystem"). The param is REQUIRED so no call
    // site can silently fall open to the guarded path; main.ts passes the real backend.
    const wantsPilot = theme === 'dusk' && backend === 'webgl2' && (opts.pilot ?? pilotEnabled());
    if (wantsPilot && sprite && GPUParticleSystem.IsSupported) {
      this.pilot = this.buildPilot(scene, sprite);
      // Digest 3(c): the freeze hook must point at the BUILT pilot, not the constructor-time
      // null — otherwise ?pilot=on keeps animating while the sim is paused (the freeze
      // carve-out's whole mechanism). An injected opts.onFreeze (tests) is left untouched.
      if (!opts.onFreeze) this.freezeHook = pilotFreezeHook(this.pilot);
    }
  }

  /** The ONE GPUParticleSystem per L3 run: box emitter + 8 attractors, blend pinned to ONEONE. */
  private buildPilot(scene: Scene, sprite: DynamicTexture): GPUParticleSystem {
    const pilot = new GPUParticleSystem(
      'l3-embers',
      { capacity: EMBER_CAPACITY, maxAttractors: PILOT_MAX_ATTRACTORS },
      scene,
    );
    pilot.particleTexture = sprite;
    // Box emitter spanning the shoulder band around the visible brazier region; the box TRACKS
    // squadZ f(snapshot) via the emitter position (update below), so emission follows the run.
    pilot.emitter = new Vector3(0, 0, 0);
    pilot.minEmitBox = new Vector3(-SHOULDER_OUT, 0.85, -EMIT_BACK);
    pilot.maxEmitBox = new Vector3(SHOULDER_OUT, 1.15, EMIT_AHEAD);
    // Upward drift velocity; the attractors layer a pull TOWARD the braziers on top (positive
    // strength, the artistic intent judged by the on/off screenshot pair).
    pilot.direction1 = new Vector3(-0.35, 1.1, -0.35);
    pilot.direction2 = new Vector3(0.35, 2.3, 0.35);
    pilot.gravity = new Vector3(0, -0.35, 0);
    pilot.minSize = 0.04;
    pilot.maxSize = 0.12;
    pilot.minLifeTime = 1.4;
    pilot.maxLifeTime = 2.8;
    pilot.emitRate = 420; // steady state ≈ 420 × 2.1 s ≈ 880 live < capacity 1200
    pilot.color1 = new Color4(2.1, 0.9, 0.3, 1);
    pilot.color2 = new Color4(1.4, 0.55, 0.18, 1);
    // THE PIN: the default ONEONE — never MULTIPLYADD (its two-draw render path would read +4
    // draws on L3-WebGL2, off the pinned +3 expectation). Assigned explicitly for the record.
    pilot.blendMode = PILOT_BLEND_MODE;
    pilot.updateSpeed = PILOT_UPDATE_SPEED;
    pilot.preWarmCycles = 0; // no preWarm: embers accumulate while the run drives to a shot
    for (let i = 0; i < PILOT_MAX_ATTRACTORS; i++) {
      const a = new Attractor();
      a.strength = 0;
      a.position = new Vector3();
      pilot.addAttractor(a);
      this.attractors.push(a);
    }
    pilot.start();
    return pilot;
  }

  /**
   * Freeze state machine (digest 3(c)). D wires the paused state in (main.ts); the transition
   * dedupe and the hook call order are pinned by ambient.test.ts against the INJECTED hook.
   */
  setFrozen(frozen: boolean): void {
    if (frozen === this.frozen) return;
    this.frozen = frozen;
    this.freezeHook(frozen);
  }

  /** Restart: clear the pool and the pilot's live particles (the fx.reset precedent). */
  reset(): void {
    this.pool?.clear();
    this.pilot?.reset();
  }

  /**
   * Rebuild every instance buffer + the pilot's emitter/attractors from the snapshot. Pure: same
   * inputs, same matrices (the pilot's particle FIELD is the disclosed carve-out — its POSE is
   * render-history-dependent; the emitter/attractor placement here is pure f(snapshot)).
   */
  update(sim: SimState): void {
    if (!this.enabled || !this.pool) return;
    // Dusk with the pilot OFF merges the fallback embers into the SAME pool (ONE ambient mesh,
    // the plan's fallback shape); with the pilot ON the pool carries dust only.
    const count = this.theme === 'dusk' ? duskPoolCount(this.pilot !== null) : this.dustCount;
    this.pool.rebuild(this.theme, count, sim, this.segmentCount);
    if (this.pilot) {
      // Freeze enforcement lives HERE (the freeze call inside ambient's update, digest 3(c)):
      // whatever order D calls setFrozen in, the pose freezes before this frame renders.
      this.freezeHook(this.frozen);
      this.pilot.emitRate = sim.phase === 'boss' ? 80 : 420;
      // Emitter box tracks squadZ: relative box [−28, +28] ⇒ world [squadZ − 4, squadZ + 52].
      (this.pilot.emitter as Vector3).set(0, 0, sim.squadZ + EMIT_CENTRE_AHEAD);
      this.repositionAttractors(sim);
    }
  }

  /** Visible braziers (camera band), nearest-8-by-z; unused attractors park at strength 0. */
  private repositionAttractors(sim: SimState): void {
    const inBand = visibleBrazierAttractors(sim.squadZ, this.segmentCount);
    for (let i = 0; i < this.attractors.length; i++) {
      const a = this.attractors[i]!;
      const b = inBand[i];
      if (b) {
        a.position.set(b.x, b.topY + 0.15, b.z);
        a.strength = EMBER_ATTRACT_STRENGTH;
      } else {
        a.strength = 0;
      }
    }
  }

  /** Probe/test: live quad instances in the level's pool (0 off High). */
  debugCounts(): number {
    return this.pool?.count ?? 0;
  }

  /** Test handle: a copy of the pool's written matrices. */
  debugMatrices(): number[] {
    return this.pool ? Array.from(this.pool.matrices.subarray(0, this.pool.count * 16)) : [];
  }

  /** Test handle: a copy of the pool's written colors. */
  debugColors(): number[] {
    return this.pool ? Array.from(this.pool.colors.subarray(0, this.pool.count * 4)) : [];
  }

  /** Probe/test: pilot facts — ember counts at shots come from `active` (getActiveCount). */
  debugPilot(): { enabled: boolean; frozen: boolean; updateSpeed: number | null; attractors: number; active: number } {
    return {
      enabled: this.pilot !== null,
      frozen: this.frozen,
      updateSpeed: this.pilot ? this.pilot.updateSpeed : null,
      attractors: this.attractors.filter((a) => a.strength > 0).length,
      active: this.pilot ? this.pilot.getActiveCount() : 0,
    };
  }
}

// ---------------------------------------------------------------------------
// The pooling pattern (debris.ts ShardPool / fx.ts QuadBatch template): one thin-instance mesh,
// matrices + colors rewritten per rebuild, count 0 ⇒ mesh disabled ⇒ 0 draws.
// ---------------------------------------------------------------------------

/** Fade weather over the nearby firing lane, retaining particles at the bridge edges and horizon. */
export function ambientLaneOpacity(x: number, relativeZ: number): number {
  const center = Math.max(0, 1 - Math.abs(x) / 4.5);
  const near = Math.max(0, Math.min(1, (32 - relativeZ) / 16));
  return 1 - 0.85 * center * near;
}

class Pool {
  readonly mesh: Mesh;
  readonly matrices: Float32Array;
  readonly colors: Float32Array;
  count = 0;
  private readonly scratch: AmbientQuad = { x: 0, y: 0, z: 0, sx: 0, sy: 0, r: 0, g: 0, b: 0, a: 0 };
  constructor(name: string, scene: Scene, sprite: DynamicTexture | null) {
    const material = new StandardMaterial(`${name}-mat`, scene);
    material.disableLighting = true;
    material.emissiveColor = new Color3(1, 1, 1);
    material.diffuseTexture = sprite;
    material.alphaMode = Constants.ALPHA_COMBINE; // soft alpha, like the fx smoke layer
    material.backFaceCulling = false;
    material.disableDepthWrite = true;
    material.fogEnabled = false; // fx-material precedent: near-camera particles are unfogged
    this.mesh = MeshBuilder.CreatePlane(name, { size: 1 }, scene);
    this.mesh.material = material;
    this.mesh.isPickable = false;
    this.mesh.alwaysSelectAsActiveMesh = true; // instances move every frame; skip bound refresh
    this.matrices = new Float32Array(AMBIENT_POOL_SIZE * 16);
    this.colors = new Float32Array(AMBIENT_POOL_SIZE * 4);
    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    this.mesh.thinInstanceSetBuffer('color', this.colors, 4, false);
    this.clear();
  }
  clear(): void {
    this.count = 0;
    this.mesh.thinInstanceCount = 0;
    this.mesh.setEnabled(false);
  }
  /** Rebuild from the snapshot: billboard basis recomputed from the chase-cam pose f(snapshot). */
  rebuild(theme: EnvTheme, count: number, sim: SimState, segmentCount: number): void {
    // The chase camera's snapshot pose (main.ts's formulas, WITHOUT the ≤ 0.12 m impulse —
    // impulse is negligible for orientation and keeps this path pure f(snapshot)).
    const cx = toWorldX(sim.squadX);
    const fx = cx * 0.8 - cx * 0.5; // target.x − position.x
    const fy = 1.1 - 4.1; // target.y − position.y
    const fz = (sim.squadZ + 11) - (sim.squadZ - 8); // target.z − position.z = 19
    const fl = Math.hypot(fx, fy, fz);
    const fxn = fx / fl;
    const fyn = fy / fl;
    const fzn = fz / fl;
    // Left-handed look-at basis (Matrix.LookAtLH): xAxis = normalize(up × z), yAxis = z × x.
    const rl = Math.hypot(fzn, fxn); // up × z = (fzn, 0, −fxn), unit only after normalizing
    const rx = fzn / rl;
    const rz = -fxn / rl;
    const ux = fyn * rz; // yAxis = z × x
    const uy = fzn * rx - fxn * rz;
    const uz = -fyn * rx;
    const q = this.scratch;
    let k = 0;
    for (let i = 0; i < count; i++) {
      if (theme === 'dusk' && i >= DUSK_DUST_COUNT) {
        emberParticle(i - DUSK_DUST_COUNT, sim, segmentCount, q);
      } else {
        fieldParticle(theme, i, sim, q);
      }
      const o = k * 16;
      const c = k * 4;
      this.matrices.fill(0, o, o + 16);
      this.matrices[o] = rx * q.sx; // row 0 = local x → camera right × sx
      this.matrices[o + 1] = 0; // right.y is 0 by construction (the camera never rolls)
      this.matrices[o + 2] = rz * q.sx;
      this.matrices[o + 4] = ux * q.sy; // row 1 = local y → camera up × sy
      this.matrices[o + 5] = uy * q.sy;
      this.matrices[o + 6] = uz * q.sy;
      this.matrices[o + 8] = fxn; // row 2 = local z → forward (the billboard normal)
      this.matrices[o + 9] = fyn;
      this.matrices[o + 10] = fzn;
      this.matrices[o + 12] = q.x;
      this.matrices[o + 13] = q.y;
      this.matrices[o + 14] = q.z;
      this.matrices[o + 15] = 1;
      this.colors[c] = q.r;
      this.colors[c + 1] = q.g;
      this.colors[c + 2] = q.b;
      this.colors[c + 3] = q.a * ambientLaneOpacity(q.x - toWorldX(sim.squadX), q.z - sim.squadZ) * (sim.phase === 'boss' ? 0.3 : 1);
      k++;
    }
    this.count = k;
    this.mesh.thinInstanceCount = k;
    this.mesh.setEnabled(k > 0);
    if (k > 0) {
      this.mesh.thinInstanceBufferUpdated('matrix');
      this.mesh.thinInstanceBufferUpdated('color');
    }
  }
}
